import React, { useEffect, useRef, useState } from 'react';
import shared from './GarageContent.module.scss';
import classes from './PurchaseCard.module.scss';

// Mirror the API's limits on the hand-off choice.
const ADDRESS_MAX = 500;
const NOTES_MAX = 1000;
const METHODS = ['pickup', 'delivery'];

/** "What to bring" for a port pickup, from the API's guide. */
export const BringList = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <div className={classes.bring}>
            <span>What to bring</span>
            <ul>
                {items.map((item) => <li key={item}>{item}</li>)}
            </ul>
        </div>
    );
};

/**
 * "Pickup or delivery?" on a purchase while the car is on its way. The two
 * options and what to bring are the API's own wording (`arrival.guide`).
 *
 * While `arrival.choiceOpen` (until the team schedules the hand-off) the
 * customer picks one and can change it; after that it is a read-only summary.
 * A delivery address is pre-filled from the purchase, else `savedAddress`
 * (the customer's profile address, when the garage has it).
 *
 * `onSubmit({ method, address, notes })` resolves to { ok, message, conflict }.
 * A conflict (the team arranged it in the meantime) is shown by the card, as
 * the reload that follows can close this section.
 */
const ArrivalChoice = ({ purchase, savedAddress = '', onSubmit }) => {
    const arrival = purchase.arrival;
    const delivery = purchase.delivery || {};
    const guide = arrival.guide || {};
    const chosen = !!arrival.requestedAt && METHODS.includes(arrival.method);
    // On a pickup, delivery.address can be the port the team entered; only a
    // delivery address is the customer's own.
    const ownAddress = delivery.method === 'delivery' ? delivery.address || '' : '';

    const [editing, setEditing] = useState(!chosen);
    const [method, setMethod] = useState(METHODS.includes(arrival.method) ? arrival.method : '');
    const [address, setAddress] = useState(ownAddress || savedAddress);
    const [notes, setNotes] = useState(delivery.customerNotes || '');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const addressTouched = useRef(false);
    const formOpen = arrival.choiceOpen && editing;

    // The profile can land after the card; fill an untouched, empty box then.
    useEffect(() => {
        if (savedAddress && !addressTouched.current) setAddress((current) => current || savedAddress);
    }, [savedAddress]);

    const startChange = () => {
        setMethod(arrival.method);
        setAddress(ownAddress || savedAddress);
        setNotes(delivery.customerNotes || '');
        setError('');
        setEditing(true);
    };

    const cancelChange = () => {
        setError('');
        setEditing(false);
    };

    const pick = (value) => {
        setMethod(value);
        setError('');
    };

    const submit = async (e) => {
        e.preventDefault();
        if (sending || !method) return;
        const trimmed = address.trim();
        if (method === 'delivery' && !trimmed) {
            setError('Add the address you want the car delivered to.');
            return;
        }
        setSending(true);
        setError('');
        const result = await onSubmit({
            method,
            address: method === 'delivery' ? trimmed : '',
            notes: notes.trim(),
        });
        setSending(false);
        // Keep what was typed when it did not go through, so nothing is lost.
        if (result.ok || result.conflict) setEditing(false);
        else setError(result.message);
    };

    const headingId = `arrival-title-${purchase._id}`;
    const isPickup = arrival.method === 'pickup';
    let choiceLabel = 'Port pickup';
    if (!isPickup) choiceLabel = delivery.address ? `Delivery to ${delivery.address}` : 'Delivery';

    return (
        <section aria-labelledby={headingId}>
            <h3 id={headingId} className={shared.subhead}>
                <i className="fas fa-route" aria-hidden="true"></i>
                Pickup or delivery?
            </h3>

            {formOpen ? (
                <form onSubmit={submit} noValidate>
                    <div className={classes.arrivalOptions} role="radiogroup" aria-labelledby={headingId}>
                        {METHODS.map((value) => {
                            const option = guide[value] || {};
                            const inputId = `arrival-${value}-${purchase._id}`;
                            const selected = method === value;
                            return (
                                <div
                                    key={value}
                                    className={`${classes.arrivalOption} ${selected ? classes.arrivalOptionSelected : ''}`}
                                >
                                    <div className={classes.arrivalOptionHead}>
                                        <input
                                            type="radio"
                                            id={inputId}
                                            name={`arrival-${purchase._id}`}
                                            value={value}
                                            checked={selected}
                                            onChange={() => pick(value)}
                                            aria-describedby={`${inputId}-summary`}
                                            disabled={sending}
                                        />
                                        <label htmlFor={inputId}>
                                            {option.title || (value === 'pickup' ? 'Pickup' : 'Delivery')}
                                        </label>
                                    </div>
                                    {option.summary && <p id={`${inputId}-summary`}>{option.summary}</p>}
                                    {value === 'pickup' && <BringList items={option.bring} />}
                                </div>
                            );
                        })}
                    </div>

                    {method === 'delivery' && (
                        <label className={classes.arrivalField}>
                            Delivery address
                            <textarea
                                value={address}
                                onChange={(e) => {
                                    addressTouched.current = true;
                                    setAddress(e.target.value);
                                }}
                                maxLength={ADDRESS_MAX}
                                rows={3}
                                autoComplete="street-address"
                                placeholder="Street, city, state and ZIP"
                                required
                                disabled={sending}
                            />
                        </label>
                    )}
                    {method && (
                        <label className={classes.arrivalField}>
                            Notes <span>(optional)</span>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                maxLength={NOTES_MAX}
                                rows={2}
                                placeholder={method === 'delivery'
                                    ? 'Gate code, best days and times…'
                                    : 'Best days for you, who\'s collecting…'}
                                disabled={sending}
                            />
                        </label>
                    )}

                    <div className={classes.arrivalActions}>
                        <button
                            type="submit"
                            className={`${shared.actionButton} ${shared.actionPrimary}`}
                            disabled={sending || !method}
                        >
                            {sending ? 'Sending…' : 'Send my choice'}
                        </button>
                        {chosen && (
                            <button type="button" className={shared.actionButton} onClick={cancelChange} disabled={sending}>
                                Cancel
                            </button>
                        )}
                    </div>
                    {error && <p className={shared.inlineError} role="alert">{error}</p>}
                </form>
            ) : chosen && (
                <div className={classes.arrivalChosen}>
                    <i className={isPickup ? 'fas fa-map-marker-alt' : 'fas fa-truck'} aria-hidden="true"></i>
                    <div>
                        <p className={classes.arrivalLead}>
                            Thanks, we've got it:{' '}
                            <strong>{choiceLabel}</strong>
                        </p>
                        {delivery.customerNotes && (
                            <p className={classes.arrivalNotes}>
                                <span>Your notes</span>
                                {delivery.customerNotes}
                            </p>
                        )}
                        {isPickup && <BringList items={guide.pickup?.bring} />}
                        {arrival.choiceOpen && (
                            <button type="button" className={classes.arrivalChange} onClick={startChange}>
                                Change
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default ArrivalChoice;
